import {
  Flex,
  HStack,
  Icon,
  VStack,
  chakra,
  useDisclosure,
} from "@chakra-ui/react";
import { Button, Card, CardProps, Loading } from "shared";

import { EmrExclamationTriangle, EmrInfusion } from "@medstonetech/slate-icons";
import { useChartPatientCardInfo } from "modules/charts-shared/api";
import { PatientDietModal } from "modules/in-process/components";
import { PatientDetailsModal } from "modules/in-process/components/in-process-patients/PatientDetailsModal";
import { DIET_TYPE_ICON } from "modules/in-process/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { ChartPatientCard } from "./ChartPatientCard";
import { usePermissions } from "contexts";

type SidebarChartPatientCardProps = {
  patientId: string;
} & CardProps;

function SidebarChartPatientCard(props: SidebarChartPatientCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scope } = usePermissions();

  const { patientId, ...cardProps } = props;
  const { data, error, isLoading } = useChartPatientCardInfo(patientId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDietOpen,
    onOpen: onDietOpen,
    onClose: onDietCLose,
  } = useDisclosure();
  const patient = data?.data;
  const showError = !!error && !isLoading;
  const showData = !!patient && !isLoading;

  const fromAllergiesMedicationsRx = location.pathname.includes(
    "allergies-medications-rx"
  );

  const fromIvStarts = location.pathname.includes("iv-starts");

  const DietIcon = DIET_TYPE_ICON[patient?.diet ?? "Healthy"];

  return (
    <VStack spacing=".25rem" w="100%" height="-webkit-fit-content">
      <Card
        boxShadow="none"
        border="none"
        width="100%"
        height="80px"
        padding="8px 14px"
        {...cardProps}
      >
        {showData && (
          <Button variant="label" onClick={onOpen}>
            <PatientDetailsModal
              isOpen={isOpen}
              onClose={onClose}
              encounterId={patientId}
            />
            <ChartPatientCard
              name={patient.fullName}
              age={patient.age}
              gender={patient.gender}
              pictureUrl={patient.pictureUrl || ""}
              overallTimer={patient.overallTime || "0:00"}
              timerStatus="AGREEABLE"
              roomNumber={patient.roomBed?.bedCode}
              acuity={patient.acuity}
              painLevel={patient.painLevel}
              dietType={patient.diet || ""}
              hasFoodAllergies={patient.hasFoodAllergies}
              entitledToObservation={patient.entitledToObservation}
              encounterId={patientId}
              isVip={patient.isVip}
              isStp={patient.painProtocol}
              chiefComplaint={patient.chiefComplaint}
              maxWidth="179px"
              isObservation={patient.isObservation}
            />
          </Button>
        )}
        {isLoading && <Loading />}
        {showError && "Something went wrong"}
      </Card>

      <Flex dir="row" flexWrap="wrap" w="100%" columnGap="5px">
        {scope("inprocess:allergies").isAccessible && (
          <Card
            cursor="pointer"
            boxShadow="none"
            padding="2px 12px"
            height="44px"
            width="32%"
            display="flex"
            flexDirection="row"
            alignSelf="flex-start"
            color={patient?.hasAllergiesRx ? "red" : " blue"}
            borderColor={
              fromAllergiesMedicationsRx
                ? patient?.hasAllergiesRx
                  ? "red"
                  : "blue"
                : "none"
            }
            borderRadius="10px"
            outline="2px solid transparent"
            outlineOffset="2px"
            onClick={() => navigate("./allergies-medications-rx")}
            {...cardProps}
          >
            <HStack spacing="0.5rem" padding="6px 0px" gap="1px">
              <Icon
                as={EmrExclamationTriangle}
                width="1.25rem"
                height="1.25rem"
              />
              <chakra.span
                fontSize=".70rem"
                maxWidth="160px"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                fontWeight="500"
              >
                Allergy <br /> & Meds
              </chakra.span>
            </HStack>
          </Card>
        )}

        {scope("inprocess:diet").isAccessible && (
          <Card
            cursor="pointer"
            boxShadow="none"
            padding="2px 12px"
            height="44px"
            width="32%"
            display="flex"
            flexDirection="row"
            alignSelf="flex-start"
            borderRadius="10px"
            outline="2px solid transparent"
            outlineOffset="2px"
            {...cardProps}
          >
            <HStack
              spacing="0.5rem"
              padding="6px 0px"
              gap="1px"
              onClick={onDietOpen}
            >
              <DietIcon width="1.25rem" height="1.25rem" />
              <chakra.span
                fontSize=".70rem"
                maxWidth="160px"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                fontWeight="500"
                color="blue"
              >
                Food <br /> & Diet
              </chakra.span>
            </HStack>
          </Card>
        )}

        {scope("inprocess:ivstarts").isAccessible && (
          <Card
            cursor="pointer"
            boxShadow="none"
            padding="2px 12px"
            height="44px"
            width="32%"
            display="flex"
            flexDirection="row"
            alignSelf="flex-start"
            borderRadius="10px"
            outline="2px solid transparent"
            outlineOffset="2px"
            borderColor={fromIvStarts ? "blue" : "none"}
            onClick={() => navigate("./iv-starts")}
            {...cardProps}
          >
            <HStack spacing="0.5rem" padding="6px 0px" gap="1px">
              <Icon
                as={EmrInfusion}
                color="blue"
                width="1.25rem"
                height="1.25rem"
              />
              <chakra.span
                fontSize=".70rem"
                maxWidth="160px"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                fontWeight="500"
                color="blue"
              >
                IV <br /> Starts
              </chakra.span>
            </HStack>
          </Card>
        )}

        {isDietOpen && patient && (
          <PatientDietModal
            isModalOpen={isDietOpen}
            onModalClose={onDietCLose}
            patient={{
              encounterId: patient?.encounterId,
              fullName: patient?.fullName,
              hasDrugAllergies: patient?.hasDrugAllergies,
              hasFoodAllergies: patient?.hasFoodAllergies,
            }}
          />
        )}
      </Flex>
    </VStack>
  );
}

export { SidebarChartPatientCard };
export type { SidebarChartPatientCardProps };
